import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SkillGapAnalysisProps {
  companyId?: string | null;
}

interface CriterionScore {
  name: string;
  avgScore: number;
  count: number;
}

export function SkillGapAnalysis({ companyId }: SkillGapAnalysisProps) {
  const { data: skillData = [], isLoading } = useQuery({
    queryKey: ['skill-gap-analysis', companyId],
    queryFn: async () => {
      // First get user IDs in this company (if filtered)
      let userIds: string[] = [];
      if (companyId) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('company_id', companyId);
        userIds = profiles?.map(p => p.id) || [];
        if (userIds.length === 0) return [];
      }

      let query = supabase
        .from('simulation_evaluations')
        .select('detailed_evaluation')
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .not('detailed_evaluation', 'is', null);

      if (companyId && userIds.length > 0) {
        // Filter by users in this company
        query = query.in('user_id', userIds);
      }

      const { data } = await query.limit(500);

      if (!data || data.length === 0) return [];

      // Aggregate scores by criterion
      const criterionMap = new Map<string, { total: number; count: number }>();

      data.forEach((evaluation) => {
        const detailed = evaluation.detailed_evaluation as any;
        if (!detailed?.criteria_scores) return;

        // Handle both array and object formats
        const scores = Array.isArray(detailed.criteria_scores) 
          ? detailed.criteria_scores 
          : Object.entries(detailed.criteria_scores).map(([name, data]: [string, any]) => ({
              criterion: name,
              score: typeof data === 'number' ? data : data?.score || 0
            }));

        scores.forEach((criterion: any) => {
          const name = criterion.criterion || criterion.name || 'Unknown';
          const score = criterion.score || 0;
          
          const existing = criterionMap.get(name) || { total: 0, count: 0 };
          criterionMap.set(name, {
            total: existing.total + score,
            count: existing.count + 1,
          });
        });
      });

      // Calculate averages and sort by lowest score (skill gaps)
      const results: CriterionScore[] = [];
      criterionMap.forEach((value, name) => {
        results.push({
          name: name.length > 25 ? name.substring(0, 22) + '...' : name,
          avgScore: Math.round(value.total / value.count),
          count: value.count,
        });
      });

      return results.sort((a, b) => a.avgScore - b.avgScore).slice(0, 8);
    },
  });

  const getBarColor = (score: number) => {
    if (score < 50) return 'hsl(var(--destructive))';
    if (score < 70) return 'hsl(var(--warning, 38 92% 50%))';
    return 'hsl(var(--primary))';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Skill Gap Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Skill Gap Analysis
        </CardTitle>
        <CardDescription>
          Lowest scoring criteria - areas needing improvement
        </CardDescription>
      </CardHeader>
      <CardContent>
        {skillData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No evaluation data available
          </div>
        ) : (
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={skillData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <XAxis 
                  type="number" 
                  domain={[0, 100]} 
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={120}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value}%`, 'Avg Score']}
                />
                <Bar dataKey="avgScore" radius={[0, 4, 4, 0]}>
                  {skillData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.avgScore)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
